export const en = {
  createOpportunityPage: {
    title: 'Create Your Own Volunteer Event',
    title_duplicate: 'Duplicate Volunteer Event',
    form: {
      steps: {
        general: {
          title: 'General Info',
          subtitle: "Let's start with the basics",
        },
        details: {
          title: 'Details',
          subtitle: 'Now for more details',
        },
        confirmation: {
          title: 'Confirmation',
          subtitle: 'Does everything look correct?',
        },
        complete: {
          title: 'Confirmation',
          subtitle: 'Volunteer Opportunity Created!',
        },
      },
      validations: {
        required: 'Required.',
        timeError: 'Start time must be before end time.',
        fixErrors: 'Please fix the errors above to continue.',
        locationError: "Add a location or select 'Virtual'.",
        memberCapacityError: 'Created events must have a minimum of 2 people.',
        shiftsError: 'You need at least 2 shifts to create an event with shifts.',
        emailError: 'Please enter a valid email address.',
        descriptionLength: 'Short description must be 255 characters or less.',
      },
      buttons: {
        back: 'Back',
        saveAndContinue: 'Save & Continue',
        submitAndCreate: 'Publish Event',
        publishChanges: 'Publish changes',
      },
    },
    fields: {
      name: {
        label: 'Name',
        placeholder: 'Enter opportunity name',
      },
      locationType: {
        label: 'Location Type',
        options: {
          inPerson: 'in person',
          virtual: 'virtual',
        },
      },
      hideListing: {
        label: 'Hide Listing',
      },
      date: {
        label: 'Date',
      },
      startTime: {
        label: 'Start Time',
      },
      endTime: {
        label: 'End Time',
      },
      timeZone: {
        label: 'Time Zone',
      },
      location: {
        label: 'Location',
      },
      memberCapacity: {
        label: 'Member Capacity',
      },
      nonprofitName: {
        label: 'Nonprofit Name',
      },
      description: {
        label: 'Description',
      },
      richTextSection: {
        label: 'Rich Text Section',
      },
      banner: {
        label: 'Banner',
      },
      confirm: {
        label: 'Confirm',
      },
    },
    warnings: {
      unsavedChanges: {
        title: 'You have unsaved changes',
        message:
          'Are you sure you want to leave? Any unsaved changes will be lost. You can find your saved progress in the My Events tab',
      },
    },
    basicDetails: {
      revert: {
        title: 'Revert to template?',
        cancel: 'Cancel',
        label: 'Revert to template',
        confirm: `This will replace your text with the template's. Continue?`,
      },
      title: "Let's start with the basics",
      nonprofitSection: {
        title: 'Nonprofit Info',
      },
      fields: {
        name: {
          label: 'Event Name',
          placeholder: 'Event Name',
        },
        groupType: {
          label: 'Group Type',
          tooltip:
            'Team is for volunteering with your department or friends. This is ideal if you have a specific group or team in mind. These events will not appear on the front page but you can share it by sharing the event URL.\n\nAnyone is open to anyone at the company and is first come first serve. Perfect for office locations where all employees can get involved.',
          options: {
            team: 'Team',
            teamHint: 'Limited to specified team members',
            anyoneHint: 'Anyone at company, first-come basis',
            anyone: 'Open Group',
          },
        },
        locationType: {
          label: 'Location Type',
          options: {
            inPerson: 'In Person',
            virtual: 'Virtual',
          },
        },
        timezone: {
          label: 'Timezone',
          placeholder: 'Select a timezone',
          change: 'Change timezone from',
          hide: 'Hide',
        },
        date: {
          label: 'Date',
          recommendations: '(recommended: {{duration}})',
        },
        startTime: {
          label: 'Start',
        },
        endTime: {
          label: 'End',
        },
        location: {
          label: 'Address',
        },
        attendees: {
          label: 'Attendees',
          placeholder: '# 10',
          hint: 'Please note that these are events meant for 2 or more people. If you are completing an event solo, you can log your volunteering hours on the <0>My Volunteering</0> page after the event!',
          recommendations: '(recommended: {{min}} - {{max}} attendees)',
        },
        memberCapacity: {
          label: 'Attendees',
          placeholder: '# 10',
        },
        eventLink: {
          label: 'Event Link (can add later if link is not yet available)',
          placeholder: 'Event link',
          tooltip:
            "Add a meeting link (ie. Teams, Meet, Zoom, etc.) for this event so your team can complete it together. This link will appear directly on the event page. You can also add this link later if it's not yet available.",
          hint: 'Link should begin with https://',
        },
        causes: {
          label: 'Causes (select a max of 3)',
          placeholder: 'Select causes',
        },
        nonprofitName: {
          label: 'Nonprofit Name',
          placeholder: 'eg. Quest Food Exchange',
        },
        nonprofitUrl: {
          label: 'Nonprofit Website URL (optional)',
          placeholder: 'eg. https://purposely.co',
        },
        nonprofitContactName: {
          label: 'Nonprofit Contact Name (optional)',
          placeholder: 'eg. John Smith',
        },
        nonprofitContactEmail: {
          label: 'Nonprofit Contact Email',
          placeholder: 'eg. john@nonprofit.org',
        },
        nonprofitContactPhone: {
          label: 'Nonprofit Contact Phone (optional)',
          placeholder: 'eg. (604) 555-1234',
        },
        communityActionType: {
          label: 'Community Action Type',
          designated: 'Designated nonprofit',
          designatedDescription: 'You pick the nonprofit now. Everyone volunteers together at one place.',
          designatedPill: 'Nonprofit set at creation',
          designatedDetail: 'Best for group events, team days, or when you have a specific partner nonprofit in mind.',
          openNonprofit: 'Open nonprofit',
          openNonprofitDescription: 'Employees volunteer anywhere locally. They log which nonprofit when submitting hours.',
          openNonprofitPill: 'Nonprofit logged at submission',
          openNonprofitDetail: 'Best for global teams or campaigns where employees volunteer at their own local organizations.',
        },
      },
    },
    review: {
      title: 'Does everything look correct?',
      subtitle:
        'Take a look at a preview of your volunteer opportunity on the right and confirm that all the details are correct. You can always come back and edit the opportunity after creating it!',
      confirmations: {
        organized:
          "I confirm that I have organized this event and the nonprofit is aware that we're attending",
        organizedVirtual:
          'I confirm that I have read all instructions found on the virtual event page',
        join: "(Optional) Check this box to join the opportunity after it's created",
      },
    },
    preview: {
      emptyState: {
        title: 'Add details to see your event page come to life!',
        description:
          "Start filling out the details of your volunteer opportunity and get a sneak peek of how it will look once it's live.",
      },
      buttons: {
        visitWebsite: 'Visit Website',
      },
      location: {
        virtual: 'Virtual Event',
        selfDirected: 'Anywhere',
        meetAt: 'Meet at',
      },
    },
    nextSteps: {
      title: 'Volunteer event created!',
      titleEdit: 'Volunteer event edited!',
      titleOngoing: 'Community action created!',
      titleEditOngoing: 'Community action edited!',
      subtitleWithLink:
        'Thanks for organizing this event! You can view or edit your event from the <0>My Requests & Created Events</0> tab',
      subtitleWithLinkOngoing:
        'Thanks for organizing this action! You can view or edit your action from the <0>My Requests & Created Events</0> tab',

      steps: {
        title: 'Next Steps',
        subtitle:
          'Here are some next steps you can take in order to spread the about your event.',
        subtitleOngoing:
          'Here are some next steps you can take in order to spread the about your action.',
        list: {
          invite: 'Invite your coworkers',
          share: 'Share this opportunity in Teams/Slack',
        },
      },
      stepsPast: {
        subtitle: 'Further personalize your event with these next steps.',
        subtitleOngoing: 'Further personalize your action with these next steps.',
        list: {
          addVolunteers: 'Add your coworkers',
          addPhotos: 'Add photos from your event',
          addPhotosOngoing: 'Add photos from your action',
        }
      },
      buttons: {
        viewOpportunity: 'View Event',
        viewOpportunityOngoing: 'View Action',
      },
    },
    confirmation: {
      title: 'Now for more details',
      fields: {
        description: {
          label: `Description For Event Card`,
          placeholder: 'Description',
          tooltip: `This is a brief description that will appear on the event's card in listings with other events`,
        },
        banner: {
          title: 'Banner Image',
          subtitle:
            'Choose from our list or upload your own. This is required for the event to be created.',
          drawer: {
            header: 'Default Images',
            subheader: 'Expand to see images',
          },
        },
      },
      dividers: {
        or: 'or',
      },
    },
    warningModal: {
      title: 'Leave this page?',
      message:
        'You have unsaved changes, are you sure you want to leave? You can always go to the Created tab in My Events to edit and publish saved drafts.',
      buttons: {
        stay: 'Stay',
        leave: 'Leave',
      },
    },
  },
}
